// Homepage.js
import React, { useEffect, useState, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDoc, setDoc, getDocs, query, where, doc, onSnapshot, deleteDoc, updateDoc, addDoc, arrayRemove, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import "../App.css";
import creditCosts from "../config/creditCosts";
import { useToast } from "../components/Toast";
import { useAuth } from "../contexts/AuthContext";
import { FaTimes, FaVideo, FaQuestionCircle, FaRegSmile, FaTrash, FaShareAlt, FaUnlink, FaPencilAlt, FaBars, FaChevronLeft, FaChevronRight, FaCopy } from "react-icons/fa";
import Tooltip from "../components/Tooltip";
import GroupFilterDropdown from "./GroupFilterDropdown";
import { loadRacerGroupFilters, saveRacerGroupFilters } from "../utils/persistentFilters";

const Homepage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [runs, setRuns] = useState([]);
  const [filteredRuns, setFilteredRuns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortType, setSortType] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [faqOpenIndex, setFaqOpenIndex] = useState(null);
  const [userData, setUserData] = useState(null);
  const [sharedRuns, setSharedRuns] = useState([]);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedRunToShare, setSelectedRunToShare] = useState(null);
  const [shareUsername, setShareUsername] = useState("");
  const [demoModalMainOpen, setDemoModalMainOpen] = useState(false);
  const [demoModalTimerOpen, setDemoModalTimerOpen] = useState(false);
  const [racers, setRacers] = useState([]);
  const [shareGroupId, setShareGroupId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // New state for Manage Racers
  const [newRacerName, setNewRacerName] = useState("");
  const [racerSortType, setRacerSortType] = useState("name"); // "name" or "group"
  const [editingRacerId, setEditingRacerId] = useState(null);
  const [renameInput, setRenameInput] = useState("");
  const [tagInputs, setTagInputs] = useState({}); // { [racerId]: inputValue }
  const [editingRunId, setEditingRunId] = useState(null);
  const [runNameEdit, setRunNameEdit] = useState("");
  const [deleteRunConfirm, setDeleteRunConfirm] = useState(null); // { runId: string, runName: string } | null

  // Racer results search/autocomplete states
  const [racerSearch, setRacerSearch] = useState("");
  const [racerSuggestions, setRacerSuggestions] = useState([]);
  const [foundRacer, setFoundRacer] = useState(null); // { id, name, eventCount }
  // Update autocomplete suggestions based on loaded racers
  useEffect(() => {
    const names = racers.map(r => r.name);
    setRacerSuggestions(names);
  }, [racers]);

  // Search run results for the selected racer
  const handleSearchRacerResults = async () => {
    if (!racerSearch.trim()) {
      showToast("Please enter a racer name.");
      return;
    }
    try {
      // Find the racer by name
      const racerData = racers.find(rd => rd.name.toLowerCase() === racerSearch.toLowerCase());
      
      if (!racerData) {
        showToast("Racer not found.");
        setFoundRacer(null);
        return;
      }

      // Count events for this racer
      // TODO: After Firebase migration, change 'events' to 'runs'
      const runsRef = collection(db, "events");
      const snapshot = await getDocs(runsRef);
      const allRuns = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      let eventCount = 0;

      allRuns.forEach(run => {
        if (run.racers) {
          const hasRacer = run.racers.some(r => r.racerId === racerData.id);
          if (hasRacer) {
            eventCount++;
          }
        }
      });

      setFoundRacer({
        id: racerData.id,
        name: racerData.name,
        eventCount: eventCount
      });
    } catch (error) {
      console.error("Error fetching racer results:", error);
      showToast("Failed to fetch racer results.");
      setFoundRacer(null);
    }
  };

  // Utility to format duration (mm:ss.hh)
  const formatDuration = (ms) => {
    if (ms == null) return "";
    const totalSeconds = ms / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const hundredths = Math.floor((ms % 1000) / 10);
    return `${minutes}:${seconds.toString().padStart(2, "0")}.${hundredths.toString().padStart(2, "0")}`;
  };


// At the top of your component, add a new state for group filtering:
const [selectedGroupFilters, setSelectedGroupFilters] = useState([]); // Array of group names
const [racerSearchQuery, setRacerSearchQuery] = useState("");
const [filtersRestored, setFiltersRestored] = useState(false);
// Add a new state to manage toggling the group input per racer
const [showGroupInput, setShowGroupInput] = useState({});
// Compute distinct groups from racers for the filter dropdown
const distinctGroups = Array.from(
  new Set(racers.flatMap((racer) => (racer.groups ? racer.groups : [])))
).sort();

const generateDiceBearAvatar = (username) => {
  return `https://api.dicebear.com/7.x/identicon/svg?seed=${encodeURIComponent(username)}`;
};

const handleUpdateRunName = async (runId, newName) => {
  try {
    // TODO: After Firebase migration, change 'events' to 'runs'
    await updateDoc(doc(db, "events", runId), { name: newName });
    setEditingRunId(null);
  } catch (error) {
    console.error("Error updating run name:", error);
  }
};


  // Sorting racers before rendering
// Sorting racers before rendering
const sortedRacers = racers.slice().sort((a, b) => {
  if (racerSortType === "name") {
    return sortOrder === "asc"
      ? a.name.localeCompare(b.name)
      : b.name.localeCompare(a.name);
  } else if (racerSortType === "created") {
    // Assumes each racer document has a 'createdAt' field (a timestamp)
    const aTime = a.createdAt ? a.createdAt.seconds : 0;
    const bTime = b.createdAt ? b.createdAt.seconds : 0;
    return bTime - aTime; // most recent first
  }
  return 0;
});

// Toggle group filter
const toggleGroupFilter = (groupName) => {
  setSelectedGroupFilters(prev => {
    if (prev.includes(groupName)) {
      return prev.filter(g => g !== groupName);
    } else {
      return [...prev, groupName];
    }
  });
};

// Clear all group filters
const clearGroupFilters = () => {
  setSelectedGroupFilters([]);
};

// Apply group filter and name search on top of the sorted list
const filteredRacers = sortedRacers.filter((racer) => {
  if (selectedGroupFilters.length === 0) return true;
  return racer.groups && racer.groups.some(group => selectedGroupFilters.includes(group));
}).filter((racer) =>
  racerSearchQuery === "" || racer.name.toLowerCase().includes(racerSearchQuery.toLowerCase())
);

  // Restore persisted filters when user changes
  useLayoutEffect(() => {
    setFiltersRestored(false);
    try {
      const userId = user?.uid || null;
      const savedFilters = loadRacerGroupFilters(userId);
      setSelectedGroupFilters(savedFilters);
    } catch (error) {
      console.error("Failed to restore homepage filters:", error);
    }
    setFiltersRestored(true);
  }, [user?.uid]);

  // Persist filters when they change
  useEffect(() => {
    if (!filtersRestored) return;
    try {
      const userId = user?.uid || null;
      saveRacerGroupFilters(userId, selectedGroupFilters);
    } catch (error) {
      console.error("Failed to persist homepage filters:", error);
    }
  }, [selectedGroupFilters, user?.uid, filtersRestored]);

  // Fetch user data (for credits)
  useEffect(() => {
    if (!user) {
      setUserData(null);
      return;
    }
    const docRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserData(snapshot.data());
      }
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate("/landing"); // change this path to your landing page route
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    const shareGroupRef = doc(db, "sharegroups", user.uid);
    getDoc(shareGroupRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          setShareGroupId(docSnap.id);
        } else {
          // Create the sharegroup document for this user with default name "public"
          setDoc(shareGroupRef, {
            ownerId: user.uid,
            name: "public",
            createdAt: Date.now(),
          })
            .then(() => {
              setShareGroupId(user.uid);
            })
            .catch((error) => {
              console.error("Error creating sharegroup:", error);
            });
        }
      })
      .catch((error) => {
        console.error("Error fetching sharegroup:", error);
      });
  }, [user]);

  // Fetch shared runs for logged-in user
  useEffect(() => {
    const fetchSharedRuns = async () => {
      try {
        const runsRef = collection(db, "runs");
        const q = query(runsRef, where("sharedWith", "array-contains", user.uid));
        const querySnapshot = await getDocs(q);
        const sharedList = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          sharedList.push({
            id: docSnap.id,
            ...data,
            date: data.timestamp?.seconds
              ? new Date(data.timestamp.seconds * 1000).toLocaleString()
              : "Unknown Date",
          });
        });
        setSharedRuns(sharedList);
      } catch (error) {
        console.error("Error fetching shared runs", error);
      }
    };
    if (user) fetchSharedRuns();
  }, [user]);

  // Fetch runs for logged-in user
  useEffect(() => {
    if (!user) return;
    
    // TODO: After Firebase migration, change 'events' to 'runs'
    const runsRef = collection(db, "events");
    
    // Query 1: Runs where user is the owner (userId)
    const q1 = query(runsRef, where("userId", "==", user.uid));
    
    // Query 2: Runs where user is in admins array
    const q2 = query(runsRef, where("admins", "array-contains", user.uid));
    
    // Store runs from each query separately
    const runsFromUserId = new Map();
    const runsFromAdmins = new Map();
    
    // Function to merge and deduplicate runs
    const mergeAndUpdateRuns = () => {
      // Combine both maps - Map automatically handles deduplication by key
      const allRunsMap = new Map();
      
      // Add runs from userId query
      runsFromUserId.forEach((run, id) => {
        allRunsMap.set(id, run);
      });
      
      // Add runs from admins query (will overwrite duplicates with same ID)
      runsFromAdmins.forEach((run, id) => {
        allRunsMap.set(id, run);
      });
      
      // Convert to array, sort, and update state
      const mergedRuns = Array.from(allRunsMap.values());
      mergedRuns.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setRuns(mergedRuns);
    };
    
    // Subscribe to userId query
    const unsubscribe1 = onSnapshot(q1, (snapshot) => {
      runsFromUserId.clear();
      snapshot.docs.forEach(docSnap => {
        runsFromUserId.set(docSnap.id, {
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      mergeAndUpdateRuns();
    }, (error) => {
      console.error("Error fetching runs by userId:", error);
    });
    
    // Subscribe to admins query
    const unsubscribe2 = onSnapshot(q2, (snapshot) => {
      runsFromAdmins.clear();
      snapshot.docs.forEach(docSnap => {
        runsFromAdmins.set(docSnap.id, {
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      mergeAndUpdateRuns();
    }, (error) => {
      console.error("Error fetching runs by admins:", error);
    });
    
    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  }, [user]);

  // Fetch racers from timer_racers collection and sort them
  useEffect(() => {
    if (!user) {
      console.log("No user, skipping racers fetch");
      setRacers([]);
      return;
    }
    
    console.log("Fetching racers for user:", user.uid, user.email);
    const racersRef = collection(db, "timer_racers");
    
    // Fetch all racers to handle cases where createdBy field might be missing or different
    const allRacersQuery = query(racersRef);
    const unsubscribeAll = onSnapshot(
      allRacersQuery, 
      (allSnapshot) => {
        console.log("Racers snapshot received, docs:", allSnapshot.docs.length);
        console.log("Snapshot metadata:", allSnapshot.metadata);
        
        if (allSnapshot.empty) {
          console.log("No racers found in database");
          setRacers([]);
          return;
        }
        
        const allRacers = allSnapshot.docs.map(docSnap => {
          const data = docSnap.data();
          console.log("Racer doc:", docSnap.id, "data:", data);
          return {
            id: docSnap.id,
            ...data
          };
        });
        
        console.log("All racers in database:", allRacers.length);
        if (allRacers.length > 0) {
          console.log("Sample racer:", allRacers[0]);
        }
        
        // Filter by createdBy, or include if createdBy is missing (might be from another app)
        const userRacers = allRacers.filter(racer => {
          const hasCreatedBy = racer.createdBy !== undefined && racer.createdBy !== null;
          const matchesUser = racer.createdBy === user.uid;
          
          if (matchesUser) return true;
          // If racer has no createdBy field, include it (might be legacy data or from another app)
          if (!hasCreatedBy) {
            console.log("Including racer without createdBy field:", racer.id, racer.name);
            return true;
          }
          return false;
        });
        
        console.log("Racers for current user:", userRacers.length, "out of", allRacers.length);
        
        userRacers.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setRacers(userRacers);
      }, 
      (error) => {
        console.error("Error fetching racers:", error);
        console.error("Error code:", error.code);
        console.error("Error message:", error.message);
        setRacers([]);
      }
    );
    
    return () => {
      console.log("Unsubscribing from racers");
      unsubscribeAll();
    };
  }, [user]);

  // Delete a run
  const handleDeleteRun = async (runId, options = {}) => {
    const runToDelete = runs.find(r => r.id === runId);
    const runName = runToDelete ? runToDelete.name : 'this run';
    
    if (!options.skipConfirm) {
      setDeleteRunConfirm({ runId, runName });
      return;
    }
    
    try {
      // TODO: After Firebase migration, change 'events' to 'runs'
      await deleteDoc(doc(db, "events", runId));
      setRuns(prevRuns => prevRuns.filter(run => run.id !== runId));
      setFilteredRuns(prevRuns => prevRuns.filter(run => run.id !== runId));
      showToast("Run deleted successfully.");
    } catch (error) {
      console.error("Error deleting run", error);
      showToast("Failed to delete run. Please try again.");
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error", error);
    }
  };

  // Search runs
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = runs.filter((run) =>
      run.runName.toLowerCase().includes(value)
    );
    setFilteredRuns(filtered);
  };

  // Sort runs
  const sortRuns = (column) => {
    let newOrder = sortOrder;
    if (sortType === column) {
      newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
    } else {
      setSortType(column);
      newOrder = "asc";
      setSortOrder("asc");
    }
    const sorted = [...filteredRuns].sort((a, b) => {
      if (column === "name") {
        return newOrder === "asc"
          ? a.runName.localeCompare(b.runName)
          : b.runName.localeCompare(a.runName);
      } else if (column === "date") {
        const aTime = a.timestamp ? a.timestamp.seconds : 0;
        const bTime = b.timestamp ? b.timestamp.seconds : 0;
        return newOrder === "asc" ? aTime - bTime : bTime - aTime;
      }
      return 0;
    });
    setFilteredRuns(sorted);
  };

  // Rename a racer
  const handleRenameRacer = async (racerId, newName) => {
    try {
      await updateDoc(doc(db, "timer_racers", racerId), { name: newName });
      setEditingRacerId(null);
    } catch (error) {
      console.error("Error renaming racer", error);
    }
  };

  const handleRemoveRacer = async (racerId) => {
    const confirmed = window.confirm(
      "Delete this racer permanently? This action cannot be undone."
    );
    if (!confirmed) {
      return;
    }

    try {
      await deleteDoc(doc(db, "timer_racers", racerId));
    } catch (error) {
      console.error("Error removing racer", error);
      showToast("Failed to delete racer. Please try again.");
    }
  };

  // Add a new racer
  const handleAddRacer = async () => {
    console.log("Adding new racer:");
    
    if (!newRacerName.trim()) {
      showToast("Please enter a racer name.");
      return;
    }
    if (userData.credits < creditCosts.CREATE_RACER) {
      showToast("Not enough credits to create a racer.");
      return;
    }
    try {
      // Deduct credits from the user.
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, { credits: userData.credits - creditCosts.CREATE_RACER });
      setUserData((prev) => ({ ...prev, credits: prev.credits - creditCosts.CREATE_RACER }));
  
      const racersRef = collection(db, "timer_racers");
      await addDoc(racersRef, { 
        name: newRacerName, 
        groups: [], 
        createdBy: user.uid  // Ensure you record who created this racer.
      });
      showToast("Racer added successfully!");
      setNewRacerName("");
    } catch (error) {
      console.error("Error adding racer", error);
      showToast("Error adding racer, please try again.");
    }
  };

  // Add a tag to a racer
  const handleAddTag = async (racerId, tag) => {
    if (!tag.trim()) return;
    try {
      await updateDoc(doc(db, "timer_racers", racerId), { groups: arrayUnion(tag) });
      setTagInputs(prev => ({ ...prev, [racerId]: "" }));
    } catch (error) {
      console.error("Error adding tag", error);
    }
  };

  // Remove a tag from a racer
  const handleRemoveTag = async (racerId, tag) => {
    try {
      await updateDoc(doc(db, "timer_racers", racerId), { groups: arrayRemove(tag) });
    } catch (error) {
      console.error("Error removing tag", error);
    }
  };

  // Toggle FAQ answer display
  const toggleFaq = (index) => {
    setFaqOpenIndex((prev) => (prev === index ? null : index));
  };


  // FAQ items
  const faqItems = [
    {
      question: "⏱️ What is Chronocult?",
      answer:
        "Chronocult is a straightforward timing system designed for amateur teams, clubs, and individuals. It offers a simple way to record race times without complicated setups, so you can focus on having fun!",
    },
    {
      question: "🚀 How do I start a new run?",
      answer:
        "To get started, simply click on the 'Create New Run' button on the homepage. Follow the on-screen instructions to set up your timing session, and you'll be off and running in just a few steps.",
    },
    {
      question: "🎯 How does the timer feature work?",
      answer:
        "Chronocult uses two devices—one at the start and one at the finish—to capture race times quickly and easily. This minimal setup keeps the process simple and user-friendly.",
    },
    {
      question: "🤝 How can I collaborate on a run?",
      answer:
        "Collaboration is easy with Chronocult. You can invite another user as an admin to your run, allowing you to share responsibilities and manage the timing session together.",
    },
    {
      question: "📤 Can I export my run results?",
      answer:
        "Yes, you can export your run results as a CSV file. This feature makes it easy to review your timings or share the data with others using your favorite spreadsheet software.",
    },
    {
      question: "💳 How do credits work?",
      answer:
        "Each timing session uses a specific number of credits that are automatically deducted from your balance. It's a simple way to keep track of your usage as you go.",
    },
    {
      question: "🛒 How do I purchase more credits?",
      answer:
        "When you need more credits, just click the 'Purchase Credits' button. The process is straightforward, and your balance updates instantly after the transaction.",
    },
    {
      question: "📁 What file formats are supported?",
      answer:
        "Chronocult currently supports exporting run data as CSV files, making it easy to import your results into other applications for further review or sharing.",
    },
    {
      question: "👥 Who can use Chronocult?",
      answer:
        "Chronocult is built for amateur teams, clubs, and individuals looking for a simple, no-frills timing solution. It's designed to be easy to set up and use, even if you're new to digital timing.",
    },
    {
      question: "🐞 How can I help improve Chronocult?",
      answer:
        "Since Chronocult is still in beta, your feedback is invaluable. If you encounter any bugs or have suggestions for improvements, please report them so we can make the app even better!",
    },
  ];

  const nameToDisplay = userData?.username || user?.displayName || user?.email || "Guest";
  const userInitials = user?.email ? user.email.match(/\b\w/g).join("").toUpperCase() : "U";

    if (!user) {
    // Optionally, you can return a loading spinner here.
    return null;
  }
  return (
    <div className="homepage-container">
      {/* Sidebar – 30% width */}
      <div className={`homepage-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>
        {!sidebarCollapsed && (
          <div className="user-panel">
              <div className="user-icon">
              <img src={generateDiceBearAvatar(user.email)} className="user-avatar" alt="User Avatar" />
                {/* {userInitials} */}
                </div>
              <div className="user-info">
                <h3>{nameToDisplay || user?.email || "Guest"}</h3>
                <p>{user?.email || "No Email"}</p>
                <p>
                  Credits: <strong>{userData?.credits ?? 0}  ⚡</strong>
                </p>
              </div>
              {user && shareGroupId && (
                <div style={{ marginTop: "10px", fontSize: "0.9em", color: "#888", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>Public Sharing URL:</span>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/runs?id=${shareGroupId}`;
                      navigator.clipboard.writeText(url).then(() => {
                        showToast("URL copied to clipboard!");
                      }).catch(() => {
                        showToast("Failed to copy URL");
                      });
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#13A1A8",
                      fontSize: "1.2em",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center"
                    }}
                    title="Copy URL to clipboard"
                  >
                    <FaCopy />
                  </button>
                  <Tooltip textKey="shareURLTooltip" />
                </div>
              )}
              <button className="primary-btn" onClick={() => navigate("/buy-credits")}>
                Purchase Credits
              </button>
              <button className="secondary-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>
        )}
      </div>

      {/* Main content – 70% width */}
      <div className={`homepage-main ${sidebarCollapsed ? 'expanded' : ''}`}>
        <div className="homepage-other-tools">
          <p>
            Our timer tool is a simple solution to record racers’ times using just two devices—one at the start and one at the finish.
          </p>
          <div style={{ marginTop: "10px", textAlign: "left" }}>
            <button
              style={{
                background: "none",
                border: "none",
                color: "#007bff",
                cursor: "pointer",
                fontSize: "16px",
              }}
              onClick={() => setDemoModalTimerOpen(true)}
            >
              <FaVideo style={{ marginRight: "5px" }} /> view demo video
            </button>
          </div>
          <br />
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />
          {/* Racer Results Search Autocomplete Section */}
          <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "1rem" }}>
            <input
              type="text"
              list="racer-suggestions"
              value={racerSearch}
              onChange={(e) => setRacerSearch(e.target.value)}
              placeholder="Search for a racer"
              style={{ flex: 1, padding: "8px", fontSize: "16px" }}
            />
            <datalist id="racer-suggestions">
              {racerSuggestions.map((name, i) => (
                <option key={i} value={name} />
              ))}
            </datalist>
            <button
              onClick={handleSearchRacerResults}
              style={{
                backgroundColor: "#13A1A8",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "4px",
                cursor: "pointer"
              }}
            >
              Search
            </button>
          </div>
          {/* Racer search result */}
          {foundRacer && (
            <>
              <div style={{ marginTop: "20px", padding: "15px", backgroundColor: "#f9f9f9", borderRadius: "5px", border: "1px solid #ddd" }}>
                <p style={{ margin: 0, fontSize: "16px", marginBottom: "10px" }}>
                  Found racer: <strong>{foundRacer.name}</strong>
                </p>
                <p style={{ margin: 0, marginBottom: "15px", color: "#666" }}>
                  {foundRacer.eventCount} event{foundRacer.eventCount !== 1 ? 's' : ''} found
                </p>
                <a
                  href={`/racer/${foundRacer.id}`}
                  style={{
                    display: "inline-block",
                    padding: "10px 20px",
                    backgroundColor: "#13A1A8",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "4px",
                    cursor: "pointer"
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/racer/${foundRacer.id}`);
                  }}
                >
                  View Racer Page
                </a>
              </div>
              <br />
              <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />
              <br />
            </>
          )}
          {foundRacer === null && racerSearch.trim() === "" && (
            <>
              <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />
              <br />
            </>
          )}
          
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
            <h2>Your Runs</h2>
            <button
              onClick={() => navigate("/timer")} // or a dedicated route to create a new run
              style={{
                background: "#13A1A8",
                border: "none",
                cursor: "pointer",
                fontSize: "1.5em",
                color: "#fff",
                borderRadius: "10%",
              }}
              title="Create New Run"
            >
              +
            </button>
          </div>
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />
          {runs.length > 0 ? (
            <div style={{ 
              maxHeight: "400px", 
              overflowY: "auto",
              border: "1px solid #ddd",
              borderRadius: "4px"
            }}>
              <ul className="runs-list" style={{ padding: 0, margin: 0 }}>
                {runs.map((run) => (
                <li
                  key={run.id}
                  className="run-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderBottom: "1px solid #eee",
                    listStyle: "none",
                    minHeight: "60px",
                  }}
                >
                  <div>
                    {editingRunId === run.id ? (
                      <input
                        type="text"
                        value={runNameEdit}
                        onChange={(e) => setRunNameEdit(e.target.value)}
                        onBlur={() => {
                          // On blur, update the run name if changed.
                          if (runNameEdit.trim() && runNameEdit !== run.name) {
                            handleUpdateRunName(run.id, runNameEdit.trim());
                          } else {
                            setEditingRunId(null);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            if (runNameEdit.trim() && runNameEdit !== run.name) {
                              handleUpdateRunName(run.id, runNameEdit.trim());
                            } else {
                              setEditingRunId(null);
                            }
                          }
                        }}
                        style={{ fontSize: "1.2em", padding: "4px" }}
                        autoFocus
                      />
                    ) : (
                      <strong 
                        style={{ 
                          fontSize: "1.1em", 
                          cursor: "pointer",
                          color: "#13A1A8",
                          textDecoration: "underline"
                        }}
                        onClick={() => navigate(`/timer?runId=${run.id}`)}
                        title="Click to select this run"
                      >
                        {run.name}
                      </strong>
                    )}
                    <br />
                    <small style={{ color: "#666", fontSize: "0.9em" }}>
                      {run.createdAt ? new Date(run.createdAt).toLocaleString() : ""}
                    </small>
                    <br />
                    <small style={{ color: "#888", fontSize: "0.8em" }}>
                      Finished: {run.racers ? run.racers.filter(r => r.startTime && r.finishTime).length : 0} / Total: {run.racers ? run.racers.length : 0}, On Course: {run.racers ? run.racers.filter(r => r.startTime && !r.finishTime).length : 0}
                    </small>
                  </div>
        <div style={{ display: "flex", alignItems: "right" }}>
        { run.userId === user.uid && (
          <>
                    {/* Edit Button */}
                    <button
            onClick={() => {
              setEditingRunId(run.id);
              setRunNameEdit(run.name);
            }}
            style={{ marginRight: "10px", width: "auto", }}
            className="primary-btn"
            title="Edit Run Name"
          >
            <FaPencilAlt />
          </button>
          <button
            onClick={() => handleDeleteRun(run.id)}
            style={{ marginRight: "10px", width: "auto", }}
            className="primary-btn"
            title="Delete Run"
          >
            <FaTrash />
          </button>
          </>
        )}
        </div>
      </li>
    ))}
              </ul>
              {runs.length > 5 && (
                <div style={{ 
                  padding: "8px", 
                  textAlign: "center", 
                  backgroundColor: "#f9f9f9",
                  borderTop: "1px solid #eee",
                  fontSize: "0.85em",
                  color: "#666"
                }}>
                  {runs.length} runs total. Scroll to see all.
                </div>
              )}
            </div>
) : (
  <p>You have no runs yet.</p>
)}
          <button className="primary-btn" onClick={() => navigate("/timer")}>
            Create New Run
          </button>
          <br />
          <br />
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />
          <br />
         
   {/* NEW: Manage Racers Section */}
<div className="manage-racers" style={{ marginBottom: "30px" }}>
  <h2>👥 Manage Racers</h2>
  
  {/* Search and Filter controls */}
  <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
    {/* Racer name search */}
    <div>
      <label htmlFor="racerSearch" style={{ marginRight: "5px" }}>Search racers:</label>
      <input
        id="racerSearch"
        type="text"
        value={racerSearchQuery}
        onChange={(e) => setRacerSearchQuery(e.target.value)}
        placeholder="Type racer name..."
        style={{ padding: "5px", width: "200px" }}
      />
    </div>
    {/* Group filter dropdown */}
    <GroupFilterDropdown
      distinctGroups={distinctGroups}
      selectedGroupFilters={selectedGroupFilters}
      onToggleGroupFilter={toggleGroupFilter}
      onClearGroupFilters={clearGroupFilters}
      label="Filter by group:"
    />
  </div>

  {/* Racers Table */}
  <div
    className="racers-list"
    style={{
      maxHeight: "600px",
      overflowY: "auto",
      border: "1px solid #ccc",
      padding: "10px",
      borderRadius: "5px",
    }}
  >
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ borderBottom: "1px solid #ccc" }}>
          <th
            style={{ textAlign: "left", cursor: "pointer", padding: "5px" }}
            onClick={() => {
              // Toggle between alphabetical and most recent created sorting.
              if (racerSortType === "name") {
                setRacerSortType("created");
                setSortOrder("desc"); // most recent first
              } else {
                setRacerSortType("name");
                setSortOrder("asc"); // alphabetical ascending
              }
            }}
          >
            Name{" "}
            {racerSortType === "name" ? (
              <span style={{ fontSize: "0.8em" }}>
                {sortOrder === "asc" ? "A→Z" : "Z→A"}
              </span>
            ) : (
              <span style={{ fontSize: "0.8em" }}>Most Recent</span>
            )}
          </th>
          <th style={{ textAlign: "left", padding: "5px" }}>Groups</th>
          <th style={{ padding: "5px", textAlign: "right" }}></th>
        </tr>
      </thead>
      <tbody>
        {filteredRacers.length > 0 ? (
          filteredRacers.map((racer) => (
            <tr key={racer.id} style={{ borderBottom: "1px solid #eee" }}>
              {/* Name column: Display name with edit button immediately following */}
              <td style={{ padding: "5px" }}>
                {editingRacerId === racer.id ? (
                  <input
                    type="text"
                    value={renameInput}
                    onChange={(e) => setRenameInput(e.target.value)}
                    onBlur={() => handleRenameRacer(racer.id, renameInput)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleRenameRacer(racer.id, renameInput);
                      }
                    }}
                    autoFocus
                    style={{ padding: "3px", width: "70%" }}
                  />
                ) : (
                  <>
                    <span>{racer.name}</span>
                    <button
                      onClick={() => {
                        setEditingRacerId(racer.id);
                        setRenameInput(racer.name);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        marginLeft: "5px",
                        color: "#555", // dark grey
                      }}
                      title="Rename Racer"
                    >
                      <FaPencilAlt />
                    </button>
                  </>
                )}
              </td>

              {/* Groups column */}
              <td style={{ padding: "5px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
                  {racer.groups && racer.groups.length > 0 ? (
                    racer.groups.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "2px 8px",
                          backgroundColor: "rgba(19, 161, 168, 0.125)",
                          color: "#13A1A8",
                          borderRadius: "12px",
                          fontSize: "0.85em",
                        }}
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(racer.id, tag)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            color: "#13A1A8",
                          }}
                          title="Remove Tag"
                        >
                          <FaTimes style={{ fontSize: "0.75em" }} />
                        </button>
                      </span>
                    ))
                  ) : (
                    <span style={{ marginRight: "5px" }}>No group</span>
                  )}
                  {showGroupInput[racer.id] ? (
                    <>
                      <input
                        type="text"
                        placeholder="New group"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleAddTag(racer.id, e.target.value);
                            setShowGroupInput((prev) => ({ ...prev, [racer.id]: false }));
                          }
                        }}
                        autoFocus
                        style={{ padding: "3px", marginTop: "5px" }}
                      />
                      <button
                        onClick={() =>
                          setShowGroupInput((prev) => ({ ...prev, [racer.id]: false }))
                        }
                        style={{
                          background: "#13A1A8", // turquoise
                          border: "none",
                          color: "#fff",
                          padding: "3px 6px",
                          borderRadius: "3px",
                          marginLeft: "5px",
                        }}
                        title="Cancel"
                      >
                        <FaTimes />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() =>
                        setShowGroupInput((prev) => ({ ...prev, [racer.id]: true }))
                      }
                      style={{
                        background: "#13A1A8", // turquoise
                        border: "none",
                        color: "#fff",
                        padding: "3px 6px",
                        borderRadius: "3px",
                        marginTop: "5px",
                        fontWeight: "bold",
                      }}
                      title="Add Group"
                    >
                      +
                    </button>
                  )}
                </div>
              </td>

              {/* Actions column: Delete button aligned right */}
              <td style={{ padding: "5px", textAlign: "right" }}>
              {racer.createdBy === user.uid && (
                <button
                  onClick={() => handleRemoveRacer(racer.id)}
            style={{ marginRight: "10px", width: "auto", }}
            className="primary-btn"
                  title="Delete Racer"
                >
                  <FaTrash />
                </button>
                )}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="3" style={{ textAlign: "center", padding: "10px" }}>
              No racers found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>

  {/* Add Racer Widget (placed outside the table) */}
  <div style={{ marginTop: "15px", display: "flex", alignItems: "center" }}>
    <input
      type="text"
      value={newRacerName}
      onChange={(e) => setNewRacerName(e.target.value)}
      placeholder="New racer name"
      style={{ padding: "5px", width: "150px", marginRight: "10px" }}
    />
    <button
      onClick={handleAddRacer}
      style={{
        padding: "5px 10px",
        backgroundColor: "#13A1A8", // turquoise
        border: "none",
        color: "#fff",
        cursor: "pointer",
        borderRadius: "3px",
      }}
    >
      Add Racer : {creditCosts.CREATE_RACER}⚡
    </button>
  </div>
</div>
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />
          <br />

          {/* FAQ Section */}
          <br />
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />
          <div className="homepage-faq">
            <h2>FAQ</h2>
            <ul className="faq-list">
              {faqItems.map((item, index) => (
                <li key={index} className="faq-item">
                  <div className="faq-question" onClick={() => toggleFaq(index)}>
                    <FaQuestionCircle style={{ marginRight: "5px" }} />
                    {item.question}
                  </div>
                  {faqOpenIndex === index && (
                    <div className="faq-answer">
                      {item.answer}{" "}
                      <span role="img" aria-label="smile">
                        <FaRegSmile />
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Delete Run Confirmation Modal */}
      {deleteRunConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteRunConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "90%", maxWidth: "420px" }}>
            <button className="modal-close-btn" onClick={() => setDeleteRunConfirm(null)}>
              <FaTimes />
            </button>
            <h3>Delete run?</h3>
            <p style={{ color: "#555" }}>
              You are about to delete <strong>"{deleteRunConfirm.runName}"</strong>.
              This action cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <button
                className="primary-btn"
                onClick={() => setDeleteRunConfirm(null)}
                style={{ flex: 1, backgroundColor: "#aaa" }}
              >
                Cancel
              </button>
              <button
                className="primary-btn"
                onClick={() => {
                  const payload = deleteRunConfirm;
                  setDeleteRunConfirm(null);
                  handleDeleteRun(payload.runId, { skipConfirm: true });
                }}
                style={{ flex: 1, backgroundColor: "#FE6C62" }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {demoModalMainOpen && (
        <div className="modal-overlay" onClick={() => setDemoModalMainOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "80%" }}>
            <button className="modal-close-btn" onClick={() => setDemoModalMainOpen(false)}>
              <FaTimes />
            </button>
            <h3>Demo Video – Video/Data Analysis Tool</h3>
            <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
              <iframe
                title="Demo Video"
                src="https://www.youtube.com/embed/VVgDmG3AIX8"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              />
            </div>
          </div>
        </div>
      )}
      {demoModalTimerOpen && (
        <div className="modal-overlay" onClick={() => setDemoModalTimerOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "80%" }}>
            <button className="modal-close-btn" onClick={() => setDemoModalTimerOpen(false)}>
              <FaTimes />
            </button>
            <h3>Demo Video – Easy Timer</h3>
            <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
              <iframe
                title="Demo Video Timer"
                src="https://www.youtube.com/embed/JhBg5TlocEw"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Homepage;